import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Form, Input, Modal, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import { FiEdit } from 'react-icons/fi';

import SkillMatrixApi from '../../Api/SkillMatrixApi';
import { toastError, toastSuccess } from '../Toast/ToastComponent';
import style from '../../Styles/Report.module.css';

/**
 * Modal for managing the skill catalog.
 *
 * Owns its own API calls (list / create / update / delete) and notifies the
 * parent via `onSaved(skills)` when the user clicks "Save Changes".
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(skills: Array<{ id: any, skill_name: string, activeStatus: boolean }>) => void} [props.onSaved]
 */
const SkillManagerModal = ({ open, onClose, onSaved }) => {
  const [skills, setSkills] = useState([]);
  const [draftSkills, setDraftSkills] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  const cancelSkillEdit = useCallback(() => {
    form.resetFields();
    setEditingKey(null);
  }, [form]);

  const loadSkills = useCallback(async () => {
    try {
      setLoading(true);
      const response = await SkillMatrixApi.getList({});
      const result = response?.data?.result;
      const list = Array.isArray(result) ? result : [];
      setSkills(list);
      setDraftSkills(list.map((s) => ({ ...s })));
    } catch (error) {
      console.error('Error fetching skills:', error);
      toastError(error?.response?.data?.message || 'Failed to load skills.');
      setSkills([]);
      setDraftSkills([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      cancelSkillEdit();
      loadSkills();
    }
  }, [open, loadSkills, cancelSkillEdit]);

  /** Sync the form field whenever the active editing target changes. */
  useEffect(() => {
    if (editingKey === null) return;
    if (editingKey === '__new__') {
      form.setFieldsValue({ skillName: '' });
      return;
    }
    const skill = draftSkills.find((s) => s.id === editingKey);
    form.setFieldsValue({ skillName: skill?.skill_name ?? '' });
  }, [editingKey]);

  const handleClose = () => {
    cancelSkillEdit();
    onClose?.();
  };

  const onToggleDraftSkill = (id, checked) => {
    setDraftSkills((prev) =>
      prev.map((s) => (s.id === id ? { ...s, activeStatus: checked } : s)),
    );
  };

  const startAddNewSkill = () => setEditingKey('__new__');
  const startEditSkill = (skill) => setEditingKey(skill.id);

  const deleteDraftSkill = (skill) => {
    setDraftSkills((prev) => prev.filter((s) => s.id !== skill.id));
    if (editingKey === skill.id) cancelSkillEdit();
  };

  const handleEditFinish = ({ skillName: rawName }) => {
    const name = String(rawName || '').trim();
    const isNew = editingKey === '__new__';

    setDraftSkills((prev) => {
      if (!isNew) {
        return prev.map((s) =>
          s.id === editingKey ? { ...s, skill_name: name.toUpperCase() } : s,
        );
      }
      const id = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      return [
        ...prev,
        { id, skill_name: name.toUpperCase(), activeStatus: true, _new: true },
      ];
    });

    cancelSkillEdit();
  };

  const submitEditForm = () => form.submit();

  const saveSkills = async () => {
    try {
      setSaving(true);

      const originalIds = new Set(skills.map((s) => s.id));
      const draftIds = new Set(draftSkills.map((s) => s.id));

      const newSkills = draftSkills.filter((s) => !originalIds.has(s.id));
      const deletedSkills = skills.filter((s) => !draftIds.has(s.id));

      const renamedSkills = [];
      const statusToggledSkills = [];

      for (const s of draftSkills) {
        const original = skills.find((os) => os.id === s.id);
        if (!original) continue;
        const nameChanged = original.skill_name !== s.skill_name;
        const statusChanged = !!original.activeStatus !== !!s.activeStatus;
        if (nameChanged) {
          renamedSkills.push(s);
        } else if (statusChanged) {
          statusToggledSkills.push(s);
        }
      }

      const tempIdToRealId = new Map();
      for (const skill of newSkills) {
        const response = await SkillMatrixApi.create({
          skill_name: skill.skill_name,
          activeStatus: !!skill.activeStatus,
        });
        const created = response?.data?.result;
        if (created?.id != null) {
          tempIdToRealId.set(skill.id, created.id);
        }
      }
      for (const skill of renamedSkills) {
        await SkillMatrixApi.update({
          id: skill.id,
          skill_name: skill.skill_name,
          activeStatus: !!skill.activeStatus,
        });
      }
      for (const skill of deletedSkills) {
        await SkillMatrixApi.delete(skill.id);
      }

      if (statusToggledSkills.length > 0) {
        const activeIds = draftSkills
          .filter((s) => !!s.activeStatus)
          .map((s) => tempIdToRealId.get(s.id) ?? s.id);
        await SkillMatrixApi.updateStatus({
          ids: activeIds,
          status: true,
        });
      }

      const savedSkills = draftSkills.map((s) => {
        const realId = tempIdToRealId.get(s.id);
        const { _new, ...rest } = s;
        return realId != null ? { ...rest, id: realId } : { ...rest };
      });

      toastSuccess('Skill matrix skills updated.');
      onSaved?.(savedSkills);
      handleClose();
    } catch (error) {
      console.error('Error saving skills:', error);
      toastError(
        error?.response?.data?.message ||
          'Failed to save skills. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const isEditingAny = editingKey !== null;

  /** Form rules that depend on current draft list / editing target. */
  const skillFieldRules = [
    { required: true, whitespace: true, message: 'Skill Name is required.' },
    {
      validator: async (_, value) => {
        const name = String(value || '').trim();
        if (!name) return;
        const exists = draftSkills.some(
          (s) =>
            s.skill_name.toLowerCase() === name.toLowerCase() &&
            s.id !== editingKey,
        );
        if (exists) throw new Error('Skill already exists.');
      },
    },
  ];

  /** Inline editor row used for both "new skill" and "edit existing skill". */
  const renderEditRow = ({
    checkboxChecked = true,
    checkboxDisabled = true,
  } = {}) => (
    <div className={style.skillMatrixModalListItem}>
      <Checkbox checked={checkboxChecked} disabled={checkboxDisabled} />
      <Form.Item
        name="skillName"
        rules={skillFieldRules}
        className={style.skillMatrixModalEditInput}
        style={{ marginBottom: 0, flex: 1 }}
        validateTrigger={['onChange', 'onBlur']}
      >
        <Input autoFocus placeholder="Enter skill name" />
      </Form.Item>
      <div className={style.skillMatrixModalEditActions}>
        <Tooltip title="Save">
          <Button
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            onClick={submitEditForm}
            htmlType="submit"
          />
        </Tooltip>
        <Tooltip title="Cancel">
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={cancelSkillEdit}
          />
        </Tooltip>
      </div>
    </div>
  );

  return (
    <Modal
      centered
      open={open}
      title="Add Skill"
      onCancel={handleClose}
      width={900}
      destroyOnClose
      maskClosable={!saving}
      closable={!saving}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={saveSkills}
            loading={saving}
            disabled={loading || isEditingAny}
          >
            Save
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        component={false}
        onFinish={handleEditFinish}
        initialValues={{ skillName: '' }}
      >
        <div className={style.skillMatrixModalBody}>
          <div className={style.skillMatrixModalToolbar}>
            <Button
              type="primary"
              icon={<MdAdd size={18} />}
              onClick={startAddNewSkill}
              disabled={isEditingAny || loading}
              title="Add Skill"
            />
          </div>

          <div className={style.skillMatrixModalList}>
            {editingKey === '__new__' && renderEditRow()}

            {!loading &&
            draftSkills.length === 0 &&
            editingKey !== '__new__' ? (
              <div className={style.skillMatrixModalEmpty}>
                No skills yet. Click "Add Skill" to create one.
              </div>
            ) : null}

            {draftSkills.map((s) => {
              const isEditing = editingKey === s.id;
              if (isEditing) {
                return (
                  <React.Fragment key={s.id}>
                    {renderEditRow({
                      checkboxChecked: !!s.activeStatus,
                      checkboxDisabled: true,
                    })}
                  </React.Fragment>
                );
              }
              return (
                <div key={s.id} className={style.skillMatrixModalListItem}>
                  <Checkbox
                    checked={!!s.activeStatus}
                    disabled={isEditingAny}
                    onChange={(e) => onToggleDraftSkill(s.id, e.target.checked)}
                  />
                  <div
                    className={style.skillMatrixModalListLabel}
                    title={s.skill_name}
                  >
                    {s.skill_name}
                  </div>
                  <button
                    type="button"
                    className={style.skillMatrixIconBtn}
                    onClick={() => startEditSkill(s)}
                    title="Edit"
                    disabled={isEditingAny}
                  >
                    <FiEdit size={16} />
                  </button>
                  <button
                    type="button"
                    className={style.skillMatrixIconBtn}
                    onClick={() => deleteDraftSkill(s)}
                    title="Delete"
                    disabled={isEditingAny}
                  >
                    <MdDeleteOutline size={18} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default SkillManagerModal;
